import type { ZipcodeData } from '@/services/zipcode';
import { toaster } from '@/shared/ui/toaster.component';

/**
 * Nomes dos campos de endereço a preencher a partir do CEP. Passados como
 * dados (não como tipos literais) porque o mesmo fluxo serve a três telas
 * com prefixos diferentes: `risk*` no Step Cliente (agregado), `risk*` no
 * Step Equipamentos (por item) e `mail*` no endereço de correspondência.
 */
export type CepLookupFields<TField extends string> = {
  address: TField;
  district: TField;
  city: TField;
  state: TField;
};

type ZipcodeResult = { success: boolean; data?: ZipcodeData; error?: string };

type LookupCepOptions<TField extends string> = {
  zipcode: string | undefined;
  // eslint-disable-next-line no-unused-vars -- nome dos parâmetros documenta o contrato
  fetchFn: (args: {
    zipcode: string;
    policyId?: string;
  }) => Promise<ZipcodeResult>;
  policyId?: string;
  fields: CepLookupFields<TField>;
  // eslint-disable-next-line no-unused-vars -- nome dos parâmetros documenta o contrato
  setFieldValue: (name: TField, value: string) => void;
  /** Chamado após um preenchimento bem-sucedido — usado para focar o campo Número. */
  onFilled?: () => void;
};

/**
 * Busca o endereço pelo CEP e preenche os campos irmãos.
 *
 * Extraído do Step Cliente quando o endereço de risco passou a existir também
 * no Step Equipamentos (por item) e no Step Frota (LMI Único) — ver spec de
 * 2026-07-19. Sem a extração, a limpeza de endereço em caso de falha (abaixo)
 * teria de ser duplicada em três telas.
 */
export async function lookupCep<TField extends string>(
  options: LookupCepOptions<TField>
): Promise<void> {
  const { zipcode, fetchFn, policyId, fields, setFieldValue, onFilled } =
    options;

  /**
   * Limpa o endereço quando a busca falha.
   *
   * Sem isto, os campos preenchidos pelo CEP ANTERIOR permaneciam e eram
   * salvos junto com o CEP novo — o registro ficava com o CEP de um lugar
   * e cidade/bairro de outro, com apenas um toast transitório avisando.
   * Importa especialmente no Local de Risco, que é o que a modalidade
   * Estacionário exige.
   */
  function clearAddress() {
    setFieldValue(fields.address, '');
    setFieldValue(fields.district, '');
    setFieldValue(fields.city, '');
    setFieldValue(fields.state, '');
  }

  const cleanCep = String(zipcode || '').replace(/\D/g, '');
  if (cleanCep.length !== 8) return;

  try {
    const result = await fetchFn({ zipcode: cleanCep, policyId });

    // A checagem de bloqueio vem ANTES da de sucesso, e não depois: a
    // resposta de um CEP bloqueado TEM a chave `street` — vazia
    // (`{cep, state:'', city:'', neighborhood:'', street:'', blocked:true}`).
    // Testar `'street' in data` primeiro classificava o bloqueio como
    // sucesso e preenchia o endereço com strings vazias, sem aviso nenhum:
    // o corretor via o CEP aceito, o endereço em branco e a modalidade
    // Estacionário continuava exigindo o local de risco, sem saída
    // (QA de 2026-08-21, CEP 40015-970 e 40140-130).
    if (result.data && (result.data as { blocked?: boolean }).blocked) {
      clearAddress();
      toaster.create({
        type: 'error',
        title: 'CEP bloqueado para cotação',
        description:
          'A seguradora não aceita risco neste CEP. Informe outro local.',
      });
    } else if (result.success && result.data && 'street' in result.data) {
      const address = result.data;
      setFieldValue(fields.address, address.street || '');
      setFieldValue(fields.district, address.neighborhood || '');
      setFieldValue(fields.city, address.city || '');
      setFieldValue(fields.state, address.state || '');
      onFilled?.();
    } else {
      clearAddress();
      toaster.create({
        type: 'error',
        title: 'CEP não encontrado',
        description: 'Verifique o CEP informado e tente novamente.',
      });
    }
  } catch (error) {
    clearAddress();
    toaster.create({
      type: 'error',
      title: 'Erro ao buscar CEP',
      description:
        error instanceof Error
          ? error.message
          : 'Verifique o CEP informado e tente novamente.',
    });
  }
}
